import { useRef, useCallback } from 'react';

export const useThrottledCallback = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number = 100,
): T => {
  const throttleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastExecutionTimeRef = useRef<number>(0);

  const throttledCallback = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const timeSinceLastExecution = now - lastExecutionTimeRef.current;

      if (timeSinceLastExecution >= delay) {
        callback(...args);
        lastExecutionTimeRef.current = now;

        if (throttleTimeoutRef.current !== null) {
          clearTimeout(throttleTimeoutRef.current);
          throttleTimeoutRef.current = null;
        }
      } else {
        if (throttleTimeoutRef.current === null) {
          const remainingTime = delay - timeSinceLastExecution;
          throttleTimeoutRef.current = setTimeout(() => {
            callback(...args);
            lastExecutionTimeRef.current = Date.now();
            throttleTimeoutRef.current = null;
          }, remainingTime);
        }
      }
    },
    [callback, delay],
  ) as T;

  return throttledCallback;
};
